import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, CreditCard, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { EtatAttestation } from './invitation/EtatAttestation';
import { RemplacerCarteBtn, carteUrl, useConfirmCarte } from './invitation/RemplacerCarteBtn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/member-invitation/membre/mes-contributions`;
const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const Contribution = ({ c, headers }) => (
  <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`contribution-${c.id}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="font-semibold text-slate-900">{c.produit} <span className="font-mono text-xs text-slate-500">{c.reference}</span></p>
      <p className="text-sm text-slate-600">Réglé {fmt(c.paye_ttc)} / {fmt(c.montant_ttc)} · reste <strong data-testid={`contribution-reste-${c.id}`}>{fmt(c.reste_ttc)}</strong></p>
    </div>
    <EtatAttestation s={c} />
    {c.echeances.length > 0 && (
      <ol className="space-y-1" data-testid={`contribution-echeancier-${c.id}`}>
        {c.echeances.map((e, i) => (
          <li key={i} className="flex justify-between text-xs">
            <span className="flex items-center gap-1 text-slate-700"><CalendarClock className="h-3 w-3 text-slate-400" />{i === 0 ? 'Acompte 60 %' : `Échéance ${i + 1}/4`} — {fmtD(e.date_echeance)}</span>
            <span className={e.statut === 'paye' ? 'text-emerald-700 font-medium' : 'text-slate-700'}>{fmt(e.montant)} {e.statut === 'paye' ? `✓ payé le ${fmtD(e.date_paiement)}` : 'à régler'}</span>
          </li>
        ))}
      </ol>
    )}
    {c.prelevement_auto?.actif && <p className="text-xs text-emerald-700" data-testid={`contribution-prelevement-${c.id}`}>Prélèvement automatique actif {c.prelevement_auto.carte ? `(${c.prelevement_auto.carte})` : ''}</p>}
    {c.reste_ttc > 0 && c.echeances.length > 0 && (
      <RemplacerCarteBtn url={carteUrl.rcds(c.id)} headers={headers} aCarte={!!c.prelevement_auto?.carte} testId={`contribution-carte-btn-${c.id}`} />
    )}
    {c.reste_ttc > 0 && c.lien_paiement && (
      <Button onClick={() => { window.location.href = c.lien_paiement; }} className={`w-full text-white ${['impaye', 'resilie'].includes(c.etat_paiement) ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid={`contribution-regulariser-btn-${c.id}`}>
        <CreditCard className="h-4 w-4 mr-2" />
        {c.etat_paiement === 'resilie' && c.regularisation ? `Régulariser par carte (${fmt(c.regularisation.total)})` : ['impaye'].includes(c.etat_paiement) ? 'Régulariser par carte' : 'Payer ma prochaine échéance'}
      </Button>
    )}
  </div>
);

export const MesContributionsRcds = ({ getAuthHeader }) => {
  const [items, setItems] = useState(null);
  const load = () => axios.get(API, { headers: getAuthHeader() }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useConfirmCarte(load);
  if (!items?.length) return null;
  return (
    <section className="rounded-xl border border-violet-200 bg-white p-5 space-y-3" data-testid="mes-contributions">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-violet-900"><ShieldCheck className="h-5 w-5" /> Mes contributions RCDS</h2>
      {items.map((c) => <Contribution key={c.id} c={c} headers={getAuthHeader()} />)}
    </section>
  );
};
